import { processSignOut } from "Auth/Hooks/useSignOut";
import {
	ApiCallError,
	type ApiQueryMeta,
	handleResponseError,
} from "Infrastructure/Api/ApiUtils";
import { AUTH_UNAUTHORIZED_EVENT } from "Infrastructure/Api/apiInterceptors";
import { CzechResourcesDictionary } from "Infrastructure/Translations/CzechResourcesDictionary";
import { zodErrorMap } from "Infrastructure/Translations/zodErrorMap";
import { initSentry } from "Infrastructure/Utils/SentryUtils";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { ModuleLayerProvider } from "Modeling/Hooks/ModuleLayerContext";
import { bootstrapTheme, useTheme } from "Shared/Hooks/useTheme";
import {
	MutationCache,
	QueryCache,
	QueryClient,
	QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { RouterProvider } from "react-router";
import { z } from "zod";
import { Toaster } from "@/components/ui/sonner";

import { appRouter } from "./Infrastructure/Routing/AppRouter";

initSentry();
bootstrapTheme();

i18n.use(initReactI18next).init({
	resources: {
		cs: CzechResourcesDictionary,
	},
	lng: "cs",
	fallbackLng: "cs",
	interpolation: {
		escapeValue: false,
	},
});

z.config({ customError: zodErrorMap });

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// Don't retry 4xx (client errors are not transient) and don't fight the
			// 401 → forced-sign-out flow. Network errors and 5xx still get retried.
			retry: (failureCount, error) => {
				if (
					error instanceof ApiCallError &&
					error.status >= 400 &&
					error.status < 500
				) {
					return false;
				}
				return failureCount < 3;
			},
		},
		mutations: {
			retry: false,
		},
	},
	queryCache: new QueryCache({
		onError: (error, query) =>
			handleResponseError(error, query.meta as ApiQueryMeta | undefined),
	}),
	mutationCache: new MutationCache({
		onError: (error, _vars, _ctx, mutation) =>
			handleResponseError(
				error,
				mutation.options.meta as ApiQueryMeta | undefined,
			),
	}),
});

window.addEventListener(AUTH_UNAUTHORIZED_EVENT, () => {
	processSignOut(queryClient);
	void appRouter.navigate(AppRouting.SignIn.path(), { replace: true });
});

function App() {
	// Mount the theme hook at the root so the prefers-color-scheme listener
	// stays active across all routes (including unauthenticated screens that
	// don't render the Topbar toggle).
	useTheme();
	return (
		<QueryClientProvider client={queryClient}>
			<ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
			<Toaster richColors position="top-left" />
			<ModuleLayerProvider>
				<RouterProvider router={appRouter} />
			</ModuleLayerProvider>
		</QueryClientProvider>
	);
}

export default App;
